import { useState } from "react";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import { FadeUp } from "@/components/Reveal";
import { ArrowButton } from "@/components/ArrowButton";
import { waLink, WHATSAPP_DISPLAY, PROFESSIONAL, slugify, INSTAGRAM_URL } from "@/data/site";
import { ArrowRight } from "lucide-react";

const GROUPS = [
  { key: "paraQuem", label: "Para quem você procura atendimento?", options: ["Para mim", "Para meu filho/minha filha", "Para outra pessoa"], testId: "para-quem" },
  { key: "atendimento", label: "O que procura?", options: ["Psicoterapia", "Avaliação Neuropsicológica", "Reavaliação Neuropsicológica", "Ainda não sei"], testId: "atendimento" },
  { key: "faixaEtaria", label: "Faixa etária", options: ["Criança", "Adolescente", "Adulto"], testId: "faixa-etaria" },
  { key: "modalidade", label: "Modalidade", options: ["Presencial", "Online", "Gostaria de orientação"], testId: "modalidade" },
];

const Chip = ({ selected, onClick, children, testId }) => (
  <button
    type="button"
    data-testid={testId}
    aria-pressed={selected}
    onClick={onClick}
    className={`rounded-full border px-4 py-2 text-[13px] tracking-wide transition-all duration-300 ${
      selected ? "border-raiz-800 bg-raiz-800 text-paper" : "border-raiz-800/25 text-raiz-900 hover:border-raiz-800"
    }`}
  >
    {children}
  </button>
);

const Contato = () => {
  const [nome, setNome] = useState("");
  const [whats, setWhats] = useState("");
  const [consent, setConsent] = useState(false);
  const [choices, setChoices] = useState({});

  const choose = (key, value) => setChoices((c) => ({ ...c, [key]: value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!nome.trim() || !whats.trim()) {
      toast.error("Preencha seu nome e WhatsApp para continuar.");
      return;
    }
    if (!consent) {
      toast.error("É necessário autorizar o contato para enviar.");
      return;
    }
    const primeiroNome = nome.trim().split(/\s+/)[0];
    const lines = [
      "Olá, Sabrina! Encontrei o seu site e gostaria de informações sobre atendimento.",
      "",
      `Nome: ${primeiroNome}`,
      `WhatsApp: ${whats.trim()}`,
      choices.paraQuem && `Atendimento para: ${choices.paraQuem}`,
      choices.atendimento && `O que procuro: ${choices.atendimento}`,
      choices.faixaEtaria && `Faixa etária: ${choices.faixaEtaria}`,
      choices.modalidade && `Modalidade: ${choices.modalidade}`,
    ].filter((l) => l !== undefined && l !== false);
    toast.success("Abrindo o WhatsApp com a sua mensagem…");
    window.open(waLink(lines.join("\n")), "_blank", "noopener,noreferrer");
  };

  const inputCls =
    "w-full rounded-2xl border border-raiz-800/20 bg-cream px-5 py-3.5 text-sm text-ink placeholder:text-ink/35 transition-colors duration-300 focus:border-raiz-700 focus:outline-none";

  return (
    <>
      <SEO
        title="Contato — Agende uma conversa | Sabrina Mota Psicóloga"
        description="Entre em contato com Sabrina Mota — Psicóloga e Neuropsicóloga — pelo WhatsApp (11) 99027-7456. Atendimento presencial e online para crianças, adolescentes e adultos."
        path="/contato"
      />
      <section className="pt-28 lg:pt-40" data-testid="contato-page">
        <div className="container-x grid gap-14 pb-20 lg:grid-cols-12 lg:pb-28">
          <div className="lg:col-span-5">
            <FadeUp>
              <p className="eyebrow" data-testid="contato-eyebrow">Contato</p>
            </FadeUp>
            <FadeUp delay={0.08}>
              <h1 className="mt-5 font-serif text-4xl font-light leading-[1.08] tracking-tight text-ink sm:text-5xl" data-testid="contato-title">
                Vamos <em className="italic text-raiz-700">conversar?</em>
              </h1>
            </FadeUp>
            <FadeUp delay={0.16}>
              <p className="lead mt-7">Conte brevemente o que você está buscando.</p>
              <p className="mt-4 text-sm leading-relaxed text-ink/60">
                Ao enviar, o WhatsApp abre com a sua mensagem já organizada. Nenhuma informação fica armazenada neste site.
              </p>
            </FadeUp>
            <FadeUp delay={0.24} className="mt-10 space-y-4 rounded-3xl border border-raiz-900/10 bg-cream p-7">
              <p className="text-xs uppercase tracking-[0.28em] text-raiz-600">Prefere chamar direto?</p>
              <a
                href={waLink()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contato-whatsapp-direto"
                className="block font-serif text-2xl text-raiz-800 underline decoration-raiz-300 underline-offset-8 transition-colors hover:text-raiz-600"
              >
                {WHATSAPP_DISPLAY}
              </a>
              <p className="text-sm text-ink/60">
                {PROFESSIONAL.name} — {PROFESSIONAL.titles}, {PROFESSIONAL.crp}
              </p>
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contato-instagram-link"
                className="inline-block text-sm text-raiz-800 underline decoration-raiz-300 underline-offset-4 transition-colors hover:text-raiz-600"
              >
                Instagram @sabrinamota.psi
              </a>
            </FadeUp>
          </div>

          <FadeUp delay={0.15} className="lg:col-span-7">
            <form onSubmit={handleSubmit} data-testid="contato-form" className="space-y-8 rounded-3xl border border-raiz-900/10 bg-paper p-6 sm:p-10">
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="nome" className="mb-2 block text-sm font-medium text-ink">Nome</label>
                  <input id="nome" data-testid="contato-nome-input" className={inputCls} value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Seu nome" autoComplete="name" />
                </div>
                <div>
                  <label htmlFor="whats" className="mb-2 block text-sm font-medium text-ink">WhatsApp</label>
                  <input id="whats" data-testid="contato-whats-input" className={inputCls} value={whats} onChange={(e) => setWhats(e.target.value)} placeholder="(11) 90000-0000" inputMode="tel" autoComplete="tel" />
                </div>
              </div>

              {GROUPS.map((g) => (
                <fieldset key={g.key}>
                  <legend className="mb-3 text-sm font-medium text-ink">{g.label}</legend>
                  <div className="flex flex-wrap gap-2.5">
                    {g.options.map((o) => (
                      <Chip
                        key={o}
                        testId={`${g.testId}-${slugify(o)}`}
                        selected={choices[g.key] === o}
                        onClick={() => choose(g.key, o)}
                      >
                        {o}
                      </Chip>
                    ))}
                  </div>
                </fieldset>
              ))}

              <label className="flex cursor-pointer items-start gap-3 text-sm text-ink/65">
                <input
                  type="checkbox"
                  data-testid="contato-consent-checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-1 h-4 w-4 accent-raiz-800"
                />
                Autorizo o contato da psicóloga Sabrina Mota pelo WhatsApp informado, conforme a{" "}
                <a href="/privacidade" className="underline underline-offset-4">Política de Privacidade</a>.
              </label>

              <button
                type="submit"
                data-testid="contato-submit-button"
                className="group inline-flex w-full items-center justify-center gap-3 rounded-full bg-raiz-800 px-7 py-4 text-sm font-medium tracking-wide text-paper transition-colors duration-300 hover:bg-raiz-700 sm:w-auto"
              >
                Enviar contato
                <ArrowRight aria-hidden="true" className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
              </button>
            </form>
          </FadeUp>
        </div>
      </section>
    </>
  );
};

export default Contato;
